export const LOCATION_MAX_AGE_MS = 5 * 60 * 1000;

function locationError(code, message) {
    return Object.assign(new Error(message), { code });
}

export function isRecentLocation(location, now = Date.now()) {
    return location && Number.isFinite(location.lat) && Math.abs(location.lat) <= 90 &&
        Number.isFinite(location.lon) && Math.abs(location.lon) <= 180 &&
        Number.isFinite(location.fetchedAt) && location.fetchedAt <= now &&
        now - location.fetchedAt <= LOCATION_MAX_AGE_MS;
}

function watchLocation(windowTarget, signal) {
    return new Promise((resolve, reject) => {
        const geolocation = windowTarget.navigator.geolocation;
        let settled = false;
        let watchId = null;
        let timer;
        const finish = (handler, value) => {
            if (settled) return;
            settled = true;
            windowTarget.clearTimeout(timer);
            if (watchId !== null) geolocation.clearWatch(watchId);
            signal.removeEventListener('abort', cancel);
            handler(value);
        };
        const cancel = () => finish(reject, new DOMException('Location request cancelled', 'AbortError'));
        if (signal.aborted) return cancel();
        signal.addEventListener('abort', cancel, { once: true });

        // Keep an explicit deadline: some browsers do not enforce a watch timeout.
        timer = windowTarget.setTimeout(() => {
            finish(reject, locationError(3, 'Geolocation timeout'));
        }, 15000);

        try {
            // Weather does not need a GPS fix. Accept a recent network/device fix
            // immediately instead of waiting for high-accuracy satellite acquisition.
            watchId = geolocation.watchPosition(
                position => finish(resolve, position),
                error => { if (error.code !== 3) finish(reject, error); },
                { enableHighAccuracy: false, maximumAge: LOCATION_MAX_AGE_MS },
            );
            if (settled) geolocation.clearWatch(watchId);
        } catch (error) {
            finish(reject, error);
        }
    });
}

/** Share one lookup across reload triggers and release the device watch on unmount. */
export function createDeviceLocator({ getCached, setCached }, windowTarget = window) {
    let inFlight = null;
    let controller;

    return {
        async get(forcePrompt = false) {
            const cached = getCached();
            if (isRecentLocation(cached)) return { lat: cached.lat, lon: cached.lon };
            if (!inFlight) {
                controller = new AbortController();
                const signal = controller.signal;
                inFlight = (async () => {
                    const navigatorTarget = windowTarget.navigator;
                    if (!windowTarget.isSecureContext || !navigatorTarget.geolocation) {
                        throw locationError('UNSUPPORTED', 'Geolocation is unavailable');
                    }
                    if (!forcePrompt && navigatorTarget.permissions?.query) {
                        let status;
                        try {
                            status = await navigatorTarget.permissions.query({ name: 'geolocation' });
                        } catch { /* Some browsers do not support this permission query. */ }
                        if (status?.state === 'prompt') {
                            throw locationError('PROMPT_REQUIRED', 'Geolocation requires user action');
                        }
                        if (status?.state === 'denied') {
                            throw locationError(1, 'Geolocation permission denied');
                        }
                    }
                    const position = await watchLocation(windowTarget, signal);
                    const coords = { lat: position.coords.latitude, lon: position.coords.longitude };
                    // Preserve the fix's original age; reading a browser-cached fix
                    // must not give it another five minutes of validity.
                    setCached({ ...coords, fetchedAt: position.timestamp });
                    return coords;
                })().finally(() => { inFlight = null; });
            }
            return inFlight;
        },
        stop() { controller?.abort(); },
    };
}
